import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import Toast from '../../../components/Common/Toast';

import MutateGate from '../../../components/Common/MutateGate';

import { getPosSeriesConfig, updatePosSeriesAssignment } from '../../../services/posSeriesConfigService';



const normalizeSeriesInput = (value) =>

  String(value || '')

    .toUpperCase()

    .replace(/\s+/g, '')

    .slice(0, 20);



const seriesCellKey = (posId, documentType) => `${posId}-${documentType}`;



const VentasPosSeriesConfig = () => {

  const [documentTypes, setDocumentTypes] = useState([]);

  const [pointsOfSale, setPointsOfSale] = useState([]);

  const [draftSeries, setDraftSeries] = useState({});

  const [loading, setLoading] = useState(true);

  const [savingKey, setSavingKey] = useState('');

  const [searchTerm, setSearchTerm] = useState('');

  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });



  const showToast = useCallback((message, type = 'info') => {

    setToast({ isOpen: true, message, type });

  }, []);



  const loadData = useCallback(async () => {

    setLoading(true);

    try {

      const res = await getPosSeriesConfig();

      if (res.success) {

        setDocumentTypes(res.data?.document_types || []);

        setPointsOfSale(res.data?.points_of_sale || []);

        setDraftSeries({});

      }

    } catch (error) {

      showToast(error.response?.data?.message || 'Error al cargar la configuración', 'error');

    } finally {

      setLoading(false);

    }

  }, [showToast]);



  useEffect(() => {

    loadData();

  }, [loadData]);



  const filteredPoints = useMemo(() => {

    const term = searchTerm.trim().toLowerCase();

    if (!term) return pointsOfSale;

    return pointsOfSale.filter(

      (pos) =>

        (pos.name || '').toLowerCase().includes(term) ||

        (pos.code || '').toLowerCase().includes(term) ||

        (pos.location || '').toLowerCase().includes(term),

    );

  }, [pointsOfSale, searchTerm]);



  const getSeriesValue = (pos, documentType) => {

    const key = seriesCellKey(pos.id, documentType);

    if (Object.prototype.hasOwnProperty.call(draftSeries, key)) {

      return draftSeries[key];

    }

    return pos.series_by_type?.[documentType] || '';

  };



  const handleSeriesInputChange = (pos, documentType, value) => {

    const key = seriesCellKey(pos.id, documentType);

    setDraftSeries((prev) => ({ ...prev, [key]: normalizeSeriesInput(value) }));

  };



  const handleSeriesSave = async (pos, documentType) => {

    const key = seriesCellKey(pos.id, documentType);

    const savedValue = pos.series_by_type?.[documentType] || '';

    const nextValue = Object.prototype.hasOwnProperty.call(draftSeries, key)

      ? draftSeries[key]

      : savedValue;



    if (nextValue === savedValue) {

      setDraftSeries((prev) => {

        const copy = { ...prev };

        delete copy[key];

        return copy;

      });

      return;

    }



    setSavingKey(key);

    try {

      const res = await updatePosSeriesAssignment(pos.id, documentType, nextValue || null);

      if (res.success) {

        const persistedSeries = res.data?.series || '';

        setPointsOfSale((prev) =>

          prev.map((row) =>

            row.id === pos.id

              ? {

                  ...row,

                  series_by_type: {

                    ...row.series_by_type,

                    [documentType]: persistedSeries || null,

                  },

                }

              : row,

          ),

        );

        setDraftSeries((prev) => {

          const copy = { ...prev };

          delete copy[key];

          return copy;

        });

        showToast('Serie actualizada', 'success');

      }

    } catch (error) {

      showToast(error.response?.data?.message || 'Error al guardar', 'error');
      setDraftSeries((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });

    } finally {

      setSavingKey('');

    }

  };



  return (

    <div className="p-6">

      <div className="max-w-6xl mx-auto">

        <div className="mb-6">

          <h1 className="text-3xl font-bold text-gray-800">Serie por punto de venta</h1>

          <p className="text-gray-600 mt-2 max-w-3xl">

            Escriba la serie de cada punto de venta según el tipo de comprobante (Factura, Boleta u Otro).
            Si la serie no existe aún, se registrará automáticamente. Puede ajustar el correlativo en{' '}

            <Link to="/configuration/document-number-series" className="text-teal-700 hover:underline">

              Gestión → Series autonumeradas

            </Link>
            .

          </p>

        </div>



        <div className="mb-4">

          <input

            type="search"

            value={searchTerm}

            onChange={(e) => setSearchTerm(e.target.value)}

            placeholder="Buscar punto de venta..."

            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"

          />

        </div>



        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">

          <table className="min-w-full divide-y divide-gray-200">

            <thead className="bg-gray-50">

              <tr>

                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">

                  Punto de venta

                </th>

                {documentTypes.map((type) => (

                  <th

                    key={type.value}

                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide min-w-[9rem]"

                  >

                    {type.label}

                  </th>

                ))}

              </tr>

            </thead>

            <tbody className="divide-y divide-gray-100">

              {loading ? (

                <tr>

                  <td colSpan={documentTypes.length + 1} className="px-4 py-8 text-center text-gray-500">

                    Cargando...

                  </td>

                </tr>

              ) : filteredPoints.length === 0 ? (

                <tr>

                  <td colSpan={documentTypes.length + 1} className="px-4 py-8 text-center text-gray-500">

                    No hay puntos de venta para mostrar.

                  </td>

                </tr>

              ) : (

                filteredPoints.map((pos) => (

                  <tr key={pos.id} className="hover:bg-gray-50/80 align-top">

                    <td className="px-4 py-3">

                      <div className="text-sm font-medium text-gray-900">{pos.name}</div>

                      <div className="text-xs text-gray-500">{pos.code}</div>

                      {pos.location ? (

                        <div className="text-xs text-gray-400 mt-0.5">{pos.location}</div>

                      ) : null}

                    </td>

                    {documentTypes.map((type) => {

                      const cellKey = seriesCellKey(pos.id, type.value);

                      const value = getSeriesValue(pos, type.value);



                      return (

                        <td key={type.value} className="px-4 py-3">

                          <MutateGate>

                            <input

                              type="text"

                              value={value}

                              onChange={(e) => handleSeriesInputChange(pos, type.value, e.target.value)}

                              onBlur={() => handleSeriesSave(pos, type.value)}

                              onKeyDown={(e) => {

                                if (e.key === 'Enter') {

                                  e.currentTarget.blur();

                                }

                              }}

                              disabled={savingKey === cellKey}

                              placeholder="Ej. F001"

                              maxLength={20}

                              className="w-full min-w-[8rem] px-3 py-2 border border-gray-300 rounded-md text-sm uppercase tracking-wide bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-60"

                              aria-label={`Serie ${type.label} para ${pos.name}`}

                            />

                          </MutateGate>

                        </td>

                      );

                    })}

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>

      </div>



      <Toast

        isOpen={toast.isOpen}

        message={toast.message}

        type={toast.type}

        onClose={() => setToast((prev) => ({ ...prev, isOpen: false }))}

      />

    </div>

  );

};



export default VentasPosSeriesConfig;


